import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const COMPANY = {
  name: 'Fabritech',
  tagline: 'Training Center',
  address: 'YYUSSA Plaza, Kisimenti — Kigali, Rwanda',
  email: 'info@fabritech.rw',
  phone: '+250 788 601 280',
  website: 'www.fabritech.rw',
  tin: '103232201',
};

const SIGNATURE_NAME = 'Sugira Fabrice';
const SIGNATURE_TITLE = 'Managing Director';

const FONT = 'helvetica';

/** Half A4 portrait (210 × 148.5 mm) — two receipts fit on one A4 when printing 2-up */
const HALF_A4 = [210, 148.5];

const INK = {
  black: [0, 0, 0],
  mid: [90, 90, 90],
  line: [160, 160, 160],
  white: [255, 255, 255],
};

const BORDER = {
  outer: [150, 150, 150],
  inner: [214, 214, 214],
  accent: [112, 112, 112],
};

const PANEL_FILL = [248, 249, 251];
const TABLE_HEAD_FILL = [236, 239, 243];
const TABLE_FOOT_FILL = [247, 248, 250];

const loadCompressedImageForPdf = async (url, { maxWidth, maxHeight, quality = 0.72 }) => {
  const response = await fetch(url);
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();

    image.onload = () => {
      try {
        const scale = Math.min(1, maxWidth / image.naturalWidth, maxHeight / image.naturalHeight);
        const width = Math.max(1, Math.round(image.naturalWidth * scale));
        const height = Math.max(1, Math.round(image.naturalHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        context.fillStyle = '#FFFFFF';
        context.fillRect(0, 0, width, height);
        context.drawImage(image, 0, 0, width, height);
        URL.revokeObjectURL(objectUrl);
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        reject(error);
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Image could not be loaded'));
    };
    image.src = objectUrl;
  });
};

export const formatInvoiceRwf = (n) =>
  `${Number(n || 0).toLocaleString('en-US')} RWF`;

const setText = (doc, [r, g, b]) => doc.setTextColor(r, g, b);
const setDraw = (doc, [r, g, b]) => doc.setDrawColor(r, g, b);
const setStyle = (doc, style = 'normal') => doc.setFont(FONT, style);

const drawBorderRect = (doc, x, y, w, h, options = {}) => {
  const {
    stroke = BORDER.inner,
    fill = null,
    lineWidth = 0.2,
  } = options;
  setDraw(doc, stroke);
  doc.setLineWidth(lineWidth);
  if (fill) {
    doc.setFillColor(...fill);
    doc.rect(x, y, w, h, 'FD');
  } else {
    doc.rect(x, y, w, h, 'S');
  }
};

const writeText = (doc, text, x, y, maxWidth) => {
  const value = String(text ?? '—');
  if (!maxWidth) {
    doc.text(value, x, y);
    return;
  }
  doc.text(doc.splitTextToSize(value, maxWidth), x, y);
};

const formatReceiptDate = (dateStr) => {
  const d = new Date(dateStr || Date.now());
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatReceiptTime = (dateStr) => {
  const d = new Date(dateStr || Date.now());
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const buildReceiptNo = (studentName, paymentDate, receiptId) => {
  const d = new Date(paymentDate || Date.now());
  const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  const suffix = receiptId
    ? String(receiptId).replace(/[^a-zA-Z0-9]/g, '').slice(-4).toUpperCase()
    : String(Date.now()).slice(-4);
  const parts = (studentName || 'Student').trim().split(/\s+/).filter(Boolean);
  const initials = parts.length >= 2
    ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
    : (parts[0] || 'ST').slice(0, 2).toUpperCase();
  return `RCP-${ymd}-${initials}${suffix}`;
};

/** Watermark ink — drawn over table with opacity for clear but non-blocking visibility */
const WATERMARK_INK = [130, 130, 130];

/**
 * Watermark centered inside the table area.
 */
const drawTableWatermark = (doc, x, y, w, h) => {
  const cx = x + w / 2;
  const cy = y + h / 2;

  setText(doc, WATERMARK_INK);
  setStyle(doc, 'bold');
  doc.setFontSize(14);
  doc.text('FABRITECH', cx, cy - 4, { align: 'center', angle: 22 });
  setStyle(doc, 'normal');
  doc.setFontSize(7);
  doc.text('OFFICIAL RECEIPT', cx, cy + 2, { align: 'center', angle: 22 });
  doc.setFontSize(6.2);
  doc.text('ORIGINAL', cx, cy + 7.5, { align: 'center', angle: 22 });

  setText(doc, INK.black);
  setStyle(doc, 'normal');
};

const applyTableWatermark = (doc, startY, endY, x, w) => {
  const height = Math.max(endY - startY, 10);
  doc.saveGraphicsState();
  if (typeof doc.GState === 'function') {
    doc.setGState(new doc.GState({ opacity: 0.24 }));
  }
  drawTableWatermark(doc, x, startY, w, height);
  doc.restoreGraphicsState();
};

export async function generatePaymentInvoicePdf({
  studentName,
  studentPhone,
  studentEmail,
  lineItems = [],
  paymentDate,
  paymentMethod,
  amountPaid,
  totalDue,
  balanceDue,
  receiptId,
}) {
  const doc = new jsPDF({
    unit: 'mm',
    format: HALF_A4,
    compress: true,
    putOnlyUsedFonts: true,
    precision: 2,
  });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 8;
  const contentW = pageW - margin * 2;
  const rightEdge = pageW - margin;

  const logoUrl = `${process.env.PUBLIC_URL || ''}/logo.png`;
  const stampUrl = `${process.env.PUBLIC_URL || ''}/Stamp%26Signature.png`;

  let logoData = null;
  let stampData = null;
  try {
    logoData = await loadCompressedImageForPdf(logoUrl, {
      maxWidth: 200,
      maxHeight: 120,
      quality: 0.82,
    });
  } catch (e) {
    console.warn('Logo load failed', e);
  }
  try {
    stampData = await loadCompressedImageForPdf(stampUrl, { maxWidth: 240, maxHeight: 240 });
  } catch (e) {
    console.warn('Stamp load failed', e);
  }

  const invoiceDate = paymentDate || new Date().toISOString();
  const receiptNo = buildReceiptNo(studentName, invoiceDate, receiptId);
  const safeLineItems = Array.isArray(lineItems) ? lineItems : [];
  const lineTotal = safeLineItems.reduce(
    (sum, item) => sum + (item.quantity || 0) * (item.unitPrice || 0),
    0
  );
  const paidAmount = amountPaid != null ? Number(amountPaid) : lineTotal;
  const dueTotal = totalDue != null ? Number(totalDue) : lineTotal;
  const remaining = balanceDue != null ? Number(balanceDue) : Math.max(0, dueTotal - paidAmount);
  const isPartial = remaining > 0 && paidAmount > 0 && paidAmount < dueTotal;
  const paymentStatus = remaining <= 0 ? 'Paid in full' : isPartial ? 'Partial' : 'Received';

  const headerY = 5;
  const logoW = 10;
  const logoH = 10;
  const companyX = margin + logoW + 3;

  if (logoData) {
    doc.addImage(logoData, 'JPEG', margin, headerY, logoW, logoH, undefined, 'FAST');
  }

  setText(doc, INK.black);
  doc.setFontSize(10);
  setStyle(doc, 'bold');
  doc.text(COMPANY.name, companyX, headerY + 3.5);
  setStyle(doc, 'normal');
  doc.setFontSize(6);
  setText(doc, INK.mid);
  doc.text(`${COMPANY.tagline} · TIN ${COMPANY.tin}`, companyX, headerY + 7);
  doc.text(`${COMPANY.phone} · ${COMPANY.email}`, companyX, headerY + 10);

  setText(doc, INK.black);
  doc.setFontSize(10);
  setStyle(doc, 'bold');
  doc.text('RECEIPT', rightEdge, headerY + 3.5, { align: 'right' });
  setStyle(doc, 'normal');
  doc.setFontSize(6);
  setText(doc, INK.mid);
  doc.text(receiptNo, rightEdge, headerY + 7.5, { align: 'right' });
  doc.text(`${formatReceiptDate(invoiceDate)} · ${formatReceiptTime(invoiceDate)}`, rightEdge, headerY + 10.5, { align: 'right' });

  let y = 18;
  setDraw(doc, BORDER.outer);
  doc.setLineWidth(0.2);
  doc.line(margin, y, rightEdge, y);
  y += 3;

  const panelH = 16;
  const panelGap = 3;
  const panelW = (contentW - panelGap) / 2;

  drawBorderRect(doc, margin, y, panelW, panelH, { fill: PANEL_FILL, stroke: BORDER.inner });
  drawBorderRect(doc, margin + panelW + panelGap, y, panelW, panelH, { fill: PANEL_FILL, stroke: BORDER.inner });

  doc.setFontSize(5.5);
  setStyle(doc, 'bold');
  setText(doc, INK.black);
  doc.text('BILL TO', margin + 2.5, y + 3.5);
  doc.text('PAYMENT', margin + panelW + panelGap + 2.5, y + 3.5);

  doc.setFontSize(7);
  writeText(doc, studentName, margin + 2.5, y + 7, panelW - 5);
  setStyle(doc, 'normal');
  doc.setFontSize(6);
  setText(doc, INK.mid);
  const contact = [studentPhone, studentEmail].filter(Boolean).join(' · ');
  if (contact) writeText(doc, contact, margin + 2.5, y + 10.5, panelW - 5);

  const detailsX = margin + panelW + panelGap + 2.5;
  doc.setFontSize(6);
  setText(doc, INK.mid);
  doc.text('Method:', detailsX, y + 7);
  doc.text('Status:', detailsX, y + 10.5);
  doc.text('Paid:', detailsX, y + 14);
  setText(doc, INK.black);
  setStyle(doc, 'bold');
  writeText(doc, paymentMethod || '—', detailsX + 11, y + 7, panelW - 14);
  writeText(doc, paymentStatus, detailsX + 11, y + 10.5, panelW - 14);
  doc.text(formatInvoiceRwf(paidAmount), detailsX + 11, y + 14);

  y += panelH + 3;

  const drawSignatureBlock = (doc, startY) => {
    const stampW = 20;
    const stampH = 22;
    const textGap = 1.5;
    const textBlockH = 7.5;

    if (stampData) {
      try {
        doc.addImage(stampData, 'JPEG', margin, startY, stampW, stampH, undefined, 'FAST');
      } catch (e) {
        setText(doc, INK.mid);
        doc.setFontSize(6);
        doc.text('Signature', margin, startY + stampH / 2);
      }
    }

    const textY = startY + (stampData ? stampH + textGap : 0);
    doc.setFontSize(6.8);
    setStyle(doc, 'bold');
    setText(doc, INK.black);
    doc.text(SIGNATURE_NAME, margin, textY + 3.5);
    setStyle(doc, 'normal');
    doc.setFontSize(6);
    setText(doc, INK.mid);
    doc.text(SIGNATURE_TITLE, margin, textY + 7);

    return startY + (stampData ? stampH + textGap : 0) + textBlockH;
  };

  const tableData = safeLineItems.map((item) => {
    const total = (item.quantity || 0) * (item.unitPrice || 0);
    return [
      item.description || '—',
      String(item.quantity || 0),
      formatInvoiceRwf(item.unitPrice || 0),
      formatInvoiceRwf(total),
    ];
  });

  const qtyW = 12;
  const priceW = 30;
  const amountW = 32;
  const descColW = contentW - qtyW - priceW - amountW;

  const tableBodyRows = tableData.length > 0
    ? tableData
    : [['Training Program', '1', formatInvoiceRwf(paidAmount), formatInvoiceRwf(paidAmount)]];

  const footRows = [];
  if (dueTotal > 0 && dueTotal !== paidAmount) {
    footRows.push([
      {
        content: 'Subtotal',
        colSpan: 3,
        styles: { halign: 'right', textColor: INK.mid, fontStyle: 'normal' },
      },
      {
        content: formatInvoiceRwf(dueTotal),
        styles: { halign: 'right', textColor: INK.black, fontStyle: 'normal' },
      },
    ]);
  }
  if (remaining > 0) {
    footRows.push([
      {
        content: 'Balance due',
        colSpan: 3,
        styles: { halign: 'right', textColor: INK.mid, fontStyle: 'normal' },
      },
      {
        content: formatInvoiceRwf(remaining),
        styles: { halign: 'right', textColor: INK.black, fontStyle: 'normal' },
      },
    ]);
  }
  footRows.push([
    {
      content: 'TOTAL PAID',
      colSpan: 3,
      styles: { halign: 'right', textColor: INK.black, fontStyle: 'bold' },
    },
    {
      content: formatInvoiceRwf(paidAmount),
      styles: { halign: 'right', textColor: INK.black, fontStyle: 'bold' },
    },
  ]);

  const tableStartY = y;

  const tableBorder = BORDER.inner;
  const tableStyles = {
    fontSize: 6.5,
    cellPadding: { top: 2.5, right: 2.5, bottom: 2.5, left: 2.5 },
    textColor: INK.black,
    fillColor: false,
    lineColor: tableBorder,
    lineWidth: 0.2,
    overflow: 'linebreak',
    valign: 'middle',
  };

  autoTable(doc, {
    startY: y,
    head: [['Description', 'Qty', 'Price', 'Amount']],
    body: tableBodyRows,
    foot: footRows,
    theme: 'grid',
    tableWidth: contentW,
    margin: { left: margin, right: margin },
    styles: tableStyles,
    headStyles: {
      fillColor: TABLE_HEAD_FILL,
      textColor: INK.black,
      fontStyle: 'bold',
      fontSize: 6,
      cellPadding: { top: 2.5, right: 2.5, bottom: 2.5, left: 2.5 },
      lineColor: BORDER.outer,
      lineWidth: { top: 0.24, right: 0.2, bottom: 0.24, left: 0.2 },
    },
    bodyStyles: {
      fillColor: INK.white,
      textColor: INK.black,
      lineColor: tableBorder,
      lineWidth: 0.2,
      minCellHeight: 7,
    },
    footStyles: {
      fillColor: TABLE_FOOT_FILL,
      textColor: INK.mid,
      fontStyle: 'normal',
      fontSize: 6.2,
      cellPadding: { top: 2, right: 2.5, bottom: 2, left: 2.5 },
      lineColor: BORDER.outer,
      lineWidth: { top: 0.2, right: 0.2, bottom: 0.2, left: 0.2 },
      minCellHeight: 5.5,
    },
    alternateRowStyles: {
      fillColor: INK.white,
    },
    didParseCell: (data) => {
      if (data.section === 'body') {
        data.cell.styles.fillColor = INK.white;
      }
      if (data.section === 'foot') {
        const isTotalRow = data.row.index === footRows.length - 1;
        data.cell.styles.minCellHeight = isTotalRow ? 6 : 5.5;
        if (data.cell.colSpan && data.cell.colSpan > 1) {
          data.cell.styles.halign = 'right';
          data.cell.styles.textColor = isTotalRow ? INK.black : INK.mid;
          data.cell.styles.fontStyle = isTotalRow ? 'bold' : 'normal';
        } else {
          data.cell.styles.halign = 'right';
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = INK.black;
        }
        if (data.cell.colSpan && data.cell.colSpan > 1) {
          data.cell.styles.fillColor = TABLE_FOOT_FILL;
        }
      }
    },
    columnStyles: {
      0: { cellWidth: descColW, fontStyle: 'normal' },
      1: { cellWidth: qtyW, halign: 'center' },
      2: { cellWidth: priceW, halign: 'right' },
      3: { cellWidth: amountW, halign: 'right', fontStyle: 'bold' },
    },
  });

  applyTableWatermark(doc, tableStartY, doc.lastAutoTable.finalY, margin, contentW);

  const footerTop = doc.lastAutoTable.finalY + 3;
  drawSignatureBlock(doc, footerTop);

  const fileSafeName = (studentName || 'Student').replace(/[^a-zA-Z0-9]/g, '_');
  const filename = `Receipt-${fileSafeName}-${receiptNo.replace(/[^a-zA-Z0-9-]/g, '')}.pdf`;
  doc.save(filename);
  return filename;
}
